import React from "react";
import { TableRow, TableCell } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, X, Pencil } from "lucide-react";

/**
 * Referral Link User Row Component
 * Modernized with shadcn TableRow and TableCell
 */
function Users_no_row(props) {
    const { userName, firstName, lastName, email, groupNumber, userId, referral_Link_Request_Status } = props;

    if (!userName) {
        return (
            <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-primary/70">
                    No Record Found
                </TableCell>
            </TableRow>
        );
    }

    // Render status badge
    const renderStatusBadge = () => {
        switch (referral_Link_Request_Status) {
            case "Requested":
                return <Badge variant="warning">Requested</Badge>;
            case "Approved":
                return <Badge variant="success">Approved</Badge>;
            case "Rejected":
                return <Badge variant="destructive">Rejected</Badge>;
            default:
                return <Badge variant="outline">{referral_Link_Request_Status}</Badge>;
        }
    };

    // Render action buttons based on status
    const renderActions = () => {
        switch (referral_Link_Request_Status) {
            case "Requested":
                return (
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            size="sm"
                            variant="success"
                            onClick={() => props.deactivate_refferals_links({ status: "Approved", userId, userName })}
                            className="gap-1"
                        >
                            <Check className="h-4 w-4" />
                            Accept
                        </Button>
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => props.deactivate_refferals_links({ status: "Rejected", userId, userName })}
                            className="gap-1"
                        >
                            <X className="h-4 w-4" />
                            Reject
                        </Button>
                    </div>
                );
            
            case "Approved":
                return (
                    <div className="flex items-center justify-end gap-2">
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => props.deactivate_refferals_links({ status: "Approvedbyadmin", userId, userName })}
                                        className="h-8 w-8 p-0"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Disable Referral Link</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                );
            
            case "Rejected":
                return (
                    <div className="flex items-center justify-end">
                        <Button
                            size="sm"
                            variant="success"
                            onClick={() => props.deactivate_refferals_links({ status: "Approved", userId, userName })}
                            className="gap-1"
                        >
                            <Check className="h-4 w-4" />
                            Accept
                        </Button>
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <TableRow className="hover:bg-muted/30">
            <TableCell className="font-medium text-primary">
                {firstName} {lastName}
            </TableCell>
            <TableCell className="text-primary/80">
                {groupNumber}
            </TableCell>
            <TableCell className="text-primary/80">
                {email}
            </TableCell>
            <TableCell>
                {renderStatusBadge()}
            </TableCell>
            <TableCell className="text-right">
                {renderActions()}
            </TableCell>
        </TableRow>
    );
}

// --- Mobile card -------------------------------------------------------------
export function ReferralCard(props) {
    const { userName, firstName, lastName, email, groupNumber, userId, referral_Link_Request_Status } = props;

    if (!userName) return null;

    const renderStatusBadge = () => {
        switch (referral_Link_Request_Status) {
            case "Requested":
                return <Badge variant="warning">Requested</Badge>;
            case "Approved":
                return <Badge variant="success">Approved</Badge>;
            case "Rejected":
                return <Badge variant="destructive">Rejected</Badge>;
            default:
                return <Badge variant="outline">{referral_Link_Request_Status}</Badge>;
        }
    };

    const renderActions = () => {
        switch (referral_Link_Request_Status) {
            case "Requested":
                return (
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="success"
                            className="flex-1 gap-1"
                            onClick={() => props.deactivate_refferals_links({ status: "Approved", userId, userName })}
                        >
                            <Check className="h-4 w-4" /> Accept
                        </Button>
                        <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1 gap-1"
                            onClick={() => props.deactivate_refferals_links({ status: "Rejected", userId, userName })}
                        >
                            <X className="h-4 w-4" /> Reject
                        </Button>
                    </div>
                );
            case "Approved":
                return (
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={() => props.deactivate_refferals_links({ status: "Approvedbyadmin", userId, userName })}
                    >
                        <Pencil className="h-4 w-4" /> Disable Referral Link
                    </Button>
                );
            case "Rejected":
                return (
                    <Button
                        size="sm"
                        variant="success"
                        className="gap-1"
                        onClick={() => props.deactivate_refferals_links({ status: "Approved", userId, userName })}
                    >
                        <Check className="h-4 w-4" /> Accept
                    </Button>
                );
            default:
                return null;
        }
    };

    const actions = renderActions();

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                {/* Header: name + status */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="font-semibold text-sm text-primary">{firstName} {lastName}</p>
                        <p className="text-xs text-muted-foreground truncate">{email}</p>
                    </div>
                    <div className="shrink-0">{renderStatusBadge()}</div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3">
                    <div>
                        <p className="text-xs text-muted-foreground">Group Number</p>
                        <p className="font-medium mt-0.5">{groupNumber || '-'}</p>
                    </div>
                </div>

                {/* Actions */}
                {actions && (
                    <div className="border-t pt-3">{actions}</div>
                )}
            </CardContent>
        </Card>
    );
}

export default Users_no_row;

